<template>
    <v-container class="pa-5" fluid>
        <v-dialog
            :value="isOpen"
            max-width="500px"
            transition="slide-y-transition"
            eager
            @keydown.esc="onClose"
            @click:outside="onClose"
        >
            <v-card>
                <v-card-title>
                    <h1 class="text-subtitle-1">
                        Wyszukiwanie kampanii
                    </h1>
                </v-card-title>
                <v-card-text>
                    <v-autocomplete
                        v-model="campaignNumber"
                        label="Numer kampanii"
                        :search-input.sync="campaignSearchQuery"
                        :filter="customCampaignFilter"
                        :loading="actionLoading.camapignSearch"
                        append-icon=""
                        item-value="number"
                        clearable
                        dense
                        outlined
                        hide-no-data
                        :hint="hintText()"
                        @click:clear="clearCampaignSearch"
                    />
                    <v-data-table
                        :headers="headers"
                        height="530"
                        :items="campaignsFound"
                        :mobile-breakpoint="0"
                        name="campaignsFound"
                        :hide-default-footer="true"
                    >
                        <template #item.number="{item}">
                            {{ item.number }}
                        </template>
                        <template #item.actions="{item}">
                            <div class="d-inline-flex">
                                <v-tooltip :open-delay="tooltipDelay" top>
                                    <template #activator="{on}">
                                        <v-btn
                                            icon
                                            :loading="actionLoading.importData[item.id]"
                                            v-on="on"
                                            @click="importData(item)"
                                        >
                                            <v-icon color="teal">
                                                mdi-import
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Importuj dane</span>
                                </v-tooltip>
                            </div>
                        </template>
                    </v-data-table>
                </v-card-text>
                <v-card-actions>
                    <v-btn
                        color="error"
                        text
                        data-sentry-label="Zamknij"
                        @click="onClose"
                    >
                        Zamknij
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import dateFormatter from '@/mixins/dateFormatter'
import tooltipDelay from '@/mixins/tooltip'
import parse from '@/mixins/parse.js'
import { errorHandler, addSentryBreadcrumb } from '@/utils/errorHandler'

let campaignSearchTimer

export default {
    name: 'ImportDataLabelsDialog',
    mixins: [
        dateFormatter,
        tooltipDelay,
        parse
    ],
    props: {
        isOpen: {
            type: Boolean
        },
        campaignsListData: {
            type: Array,
            default: () => []
        },
    },
    data() {
        return {
            campaignSearchQuery: null,
            campaignSearchQueryMinLength: 3,
            minLengthPassed: false,
            campaignsFound: [],
            actionLoading: {
                camapignSearch: false,
                importData: {}
            },
            importedData: [],
            campaignNumber: null,
            headers: [
                {
                    text: 'Numer kampanii',
                    value: 'number',
                    align: 'center',
                    sortable: false,
                    width: 70
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    align: 'center',
                    sortable: false,
                    width: 30
                }
            ]
        }
    },
    watch: {
        campaignSearchQuery(value = null) {
            if (value === null || value.length < this.campaignSearchQueryMinLength) {
                this.minLengthPassed = false
                this.camapignsFound = []
                
                return
            }

            this.actionLoading.campaignSearch = true
            this.minLengthPassed = true

            clearTimeout(campaignSearchTimer)

            campaignSearchTimer = setTimeout(() => {
                if (value) {
                    this.searchCampaigns(value)
                        .catch(error => {
                            errorHandler(error)
                        })
                } else {
                    this.actionLoading.camapaignSearch = false
                    this.campaignsFound = []
                }
            }, 500)
        },
        isOpen(value) {
            value && this.searchLastClosedCampaign()
        }
    },
    methods: {
        onClose() {
            this.$emit('on-close')
            this.clearCampaignSearch()
        },
        onImport() {
            this.$emit('on-import', this.importedData)
            this.onClose()
        },
        customCampaignFilter(item, queryText) {
            const campaign = item.number.toLowerCase(),
                search = queryText.toLowerCase()

            return campaign.indexOf(search) > -1
        },
        hintText() {
            if (!this.minLengthPassed) {
                return `Proszę wpisać minimum ${this.campaignSearchQueryMinLength} znaki.`
            }
        },
        importData(campaign) {
            this.$set(this.actionLoading.importData, campaign.id, true)
            this.$store.dispatch('campaigns/getCampaign', campaign.id)
                .then(response => {
                    this.importedData = response.data.productSets.map(({ EAN, index, name, sumForCampaign }) => ({
                        shopFormat: name,
                        shopIndex: parseInt(index),
                        ean: EAN,
                        copies: sumForCampaign
                    }))
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.$set(this.actionLoading.importData, campaign.id, false)
                    this.onImport()
                })
        },
        clearCampaignSearch() {
            this.campaignSearchQuery = null
            this.campaignsFound = []

            this.searchLastClosedCampaign()
        },
        async searchCampaigns(campaignNumber = '') {
            addSentryBreadcrumb('ui.action', 'Wyszukiwanie kampanii')

            return await this.$store.dispatch('campaigns/findCampaign', {number: campaignNumber})
                .then(response => {
                    this.campaignsFound = response.data.results
                    
                    return response.data.results
                })
                .catch(error => {
                    throw error
                })
                .finally(() => {
                    this.actionLoading.campaignSearch = false
                })
        },
        searchLastClosedCampaign() {
            addSentryBreadcrumb('ui.action', 'Wyszukiwanie ostatniej zamkniętej kampanii')

            this.$store.dispatch('campaigns/findLastClosedCampaign')
                .then(response => {
                    this.campaignsFound = response.data.results

                    return response.data.results
                })
                .catch(error => {
                    throw error
                })
        }
    }
}
</script>